// ============================================================
// ByOurConsent.com -- AI Bill Analysis
// /premium/bill-analysis
// Premium member tool -- Paperwork as a Weapon Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

// ============================================================
// Data
// ============================================================
const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const FEDERAL_BILL_TYPES = [
    'HR (House Bill)',
    'S (Senate Bill)',
    'HJRes (House Joint Resolution)',
    'SJRes (Senate Joint Resolution)',
    'HCRes (House Concurrent Resolution)',
    'SCRes (Senate Concurrent Resolution)',
    'HRes (House Simple Resolution)',
    'SRes (Senate Simple Resolution)',
];

const ANALYSIS_TYPES = [
    { id: 'summary',       label: 'Plain English summary',          desc: 'What does this bill actually do? Cut through the legalese.' },
    { id: 'constitutional',label: 'Constitutional concerns',        desc: 'Does it violate the Constitution? What amendments or articles are at risk?' },
    { id: 'pork',          label: 'Pork & hidden riders',           desc: 'Unrelated spending, earmarks, and provisions buried in the text.' },
    { id: 'fiscal',        label: 'Fiscal impact',                  desc: 'What does it cost? Who pays? How is it funded?' },
    { id: 'opposition',    label: 'Opposition language',            desc: 'Ready-made arguments and language for public comments, letters, and town halls.' },
    { id: 'sponsors',      label: 'Sponsor & voting context',       desc: 'Who introduced it, who supports it, and what similar bills have passed or failed.' },
];

// ============================================================
// Step indicator
// ============================================================
const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div
                    onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: i + 1 <= step ? '#8b1a1a' : '#ede8dc',
                        border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600,
                        color: i + 1 <= step ? '#f5f0e8' : '#8a8070',
                        cursor: i + 1 < step ? 'pointer' : 'default'
                    }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />
                )}
            </React.Fragment>
        ))}
    </div>
);

// ============================================================
// Styles
// ============================================================
const iStyle = {
    width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif',
    color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box'
};

const lStyle = {
    fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080',
    letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px'
};

const btnPrimary = {
    background: '#8b1a1a', color: '#f5f0e8', border: 'none',
    padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif',
    letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer'
};

const btnSecondary = {
    background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa',
    padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif',
    borderRadius: '3px', cursor: 'pointer'
};

// ============================================================
// Main component
// ============================================================
const BillAnalysisPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        level:        'federal',
        inputMethod:  'number',   // number | paste
        billType:     '',
        billNumber:   '',
        billSession:  '',
        state:        '',
        stateBillNum: '',
        billText:     '',
        billTitle:    '',
        analysisTypes: ['summary', 'constitutional', 'pork', 'fiscal', 'opposition'],
        context:      ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const toggleAnalysis = (id) => {
        setForm(p => ({
            ...p,
            analysisTypes: p.analysisTypes.includes(id)
                ? p.analysisTypes.filter(a => a !== id)
                : [...p.analysisTypes, id]
        }));
    };

    const canProceedStep1 = () => {
        if (form.inputMethod === 'paste') return form.billText.trim().length > 100;
        if (form.level === 'federal') return form.billType && form.billNumber;
        return form.state && form.stateBillNum;
    };

    // ============================================================
    // Generate analysis via backend
    // ============================================================
    const generate = async () => {
        setLoading(true);
        setError('');

        const selectedAnalysis = ANALYSIS_TYPES
            .filter(a => form.analysisTypes.includes(a.id))
            .map(a => a.label)
            .join(', ');

        let billIdentifier = '';
        if (form.inputMethod === 'paste') {
            billIdentifier = form.billTitle || 'Provided bill text';
        } else if (form.level === 'federal') {
            billIdentifier = `${form.billType.split(' ')[0]} ${form.billNumber}${form.billSession ? `, ${form.billSession} Congress` : ''}`;
        } else {
            billIdentifier = `${form.state} ${form.stateBillNum}`;
        }

        const billSource = form.inputMethod === 'paste'
            ? `BILL TEXT PROVIDED:\n\n${form.billText}`
            : `BILL IDENTIFIER: ${billIdentifier}\n\nNote: Analyze based on your knowledge of this bill. If you are not familiar with the specific bill number, provide analysis based on the bill type, common provisions in similar legislation, and constitutional principles.`;

        const prompt = `You are an expert constitutional law analyst, legislative researcher, and civic educator. Analyze the following bill and provide a comprehensive report.

${billSource}

${form.context ? `ADDITIONAL CONTEXT FROM REQUESTER: ${form.context}` : ''}

REQUESTED ANALYSIS: ${selectedAnalysis}

Provide a thorough analysis covering each requested section. Format your response clearly with headers for each section.

${form.analysisTypes.includes('summary') ? `
## PLAIN ENGLISH SUMMARY
What does this bill actually do? Explain it clearly as if to an intelligent citizen with no legal background. Include:
- The main purpose and effect
- Who it affects and how
- Key provisions explained simply
- What changes from current law
` : ''}

${form.analysisTypes.includes('constitutional') ? `
## CONSTITUTIONAL CONCERNS
Analyze constitutional implications:
- Which constitutional provisions (amendments, articles, clauses) are implicated
- Any provisions that may exceed congressional/legislative authority
- Separation of powers concerns
- Individual rights implications
- Relevant Supreme Court precedents or case law
- Rate the constitutional risk: Low / Moderate / High / Severe
` : ''}

${form.analysisTypes.includes('pork') ? `
## PORK & HIDDEN RIDERS
Identify:
- Spending provisions unrelated to the bill's stated purpose
- Earmarks for specific districts, states, or entities
- Provisions buried in the text that received little public debate
- Last-minute additions or amendments
- Who benefits from these provisions
` : ''}

${form.analysisTypes.includes('fiscal') ? `
## FISCAL IMPACT
Analyze the financial implications:
- Estimated cost to taxpayers
- How it is funded (new taxes, debt, reallocation)
- Long-term fiscal trajectory
- Winners and losers financially
- Comparison to similar past legislation
` : ''}

${form.analysisTypes.includes('opposition') ? `
## OPPOSITION LANGUAGE
Provide ready-made language citizens can use:
- Key talking points against this bill (if concerns exist)
- Language for public comments to government agencies
- Template language for letters to representatives
- Arguments for town halls and public forums
- Questions to demand answers to
Note: If the bill appears largely constitutional and beneficial, note that and provide balanced analysis instead.
` : ''}

${form.analysisTypes.includes('sponsors') ? `
## SPONSOR & VOTING CONTEXT
Provide context about:
- Who introduced this bill and their record
- Key co-sponsors and their interests
- Similar bills that have passed or failed and why
- What groups support and oppose this bill
- Likelihood of passage based on current political context
` : ''}

## BOTTOM LINE
A 2-3 sentence summary of the most important things citizens need to know about this bill.

Be thorough, accurate, and cite specific constitutional provisions where relevant. If you are uncertain about specific details of a bill identified only by number, clearly note that and provide analysis based on available information and constitutional principles.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('boc_token')}`
                },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');

            setResult({ analysis: text, billIdentifier, analysisTypes: form.analysisTypes });
            setStep(3);
        } catch (err) {
            setError('Failed to generate analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // Download as RTF
    // ============================================================
    const downloadWord = () => {
        downloadReportAsWord(
            result.analysis,
            `Bill-Analysis-${result.billIdentifier.replace(/[^a-z0-9]/gi, '-').slice(0, 40)}`
        );
    };

    const reset = () => {
        setStep(1); setResult(null); setError('');
        setForm({ level: 'federal', inputMethod: 'number', billType: '', billNumber: '',
            billSession: '', state: '', stateBillNum: '', billText: '', billTitle: '',
            analysisTypes: ['summary', 'constitutional', 'pork', 'fiscal', 'opposition'], context: '' });
    };

    // ============================================================
    // Render
    // ============================================================
    return (
        <>
            <Helmet><title>AI Bill Analysis — By Our Consent</title></Helmet>
            <Nav />

            {/* Header */}
            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Premium tools
                    </Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>
                        Paperwork as a weapon
                    </div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>
                        AI Bill Analysis
                    </h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Enter any federal or state bill and get a plain-English breakdown —
                        including constitutional concerns, hidden pork, fiscal impact,
                        and ready-made opposition language.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {/* ── STEP 1: Bill identification ── */}
                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — Identify the bill
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Bill number vs pasted text — which is better?</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Entering a bill number works well for major, well-known legislation. For best results — especially
                                    for newer, obscure, or state bills — paste the actual bill text. The AI can only analyze what it
                                    has been trained on when using bill numbers alone.
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> For HR 1 (major federal bill) — bill number works fine.
                                    For a state bill introduced last month — paste the text for accurate analysis.
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>How to find and copy bill text</div>
                                <div style={{ marginBottom: '6px' }}>
                                    Federal bills: Go to <a href="https://congress.gov" target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}>congress.gov</a> →
                                    search the bill name or number → click the bill → click the "Text" tab →
                                    select "Shown as: Text" → select all and copy (Ctrl+A, Ctrl+C).
                                </div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Tip:</strong> You don't need to paste the entire bill.
                                    Even pasting the key sections (findings, definitions, and operative provisions)
                                    gives the AI enough to produce a thorough analysis.
                                </div>

                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Additional context on Step 2 — focus the analysis</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Focus on how Section 4 affects small business owners in rural areas"
                                    or "I am particularly concerned about the 4th Amendment implications of the data collection provisions."
                                </div>
                            </div>
                        </details>

                        {/* Federal or State */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Level</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {[['federal','Federal bill'],['state','State bill']].map(([val, label]) => (
                                    <button key={val} onClick={() => set('level', val)}
                                        style={{ flex: 1, padding: '10px', background: form.level === val ? '#8b1a1a' : '#f5f0e8', color: form.level === val ? '#f5f0e8' : '#4a5060', border: `0.5px solid ${form.level === val ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Input method */}
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>How would you like to identify the bill?</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {[['number','Enter bill number'],['paste','Paste full bill text']].map(([val, label]) => (
                                    <button key={val} onClick={() => set('inputMethod', val)}
                                        style={{ flex: 1, padding: '10px', background: form.inputMethod === val ? '#1e2a3a' : '#f5f0e8', color: form.inputMethod === val ? '#e8dfc8' : '#4a5060', border: `0.5px solid ${form.inputMethod === val ? '#1e2a3a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '4px' }}>
                                {form.inputMethod === 'number'
                                    ? 'AI will analyze based on its knowledge of the bill. For best results, use Paste full bill text.'
                                    : 'Copy and paste the actual legal text of the bill from congress.gov or your state legislature website.'}
                            </div>
                        </div>

                        {/* Federal bill by number */}
                        {form.inputMethod === 'number' && form.level === 'federal' && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Bill type</label>
                                    <select style={iStyle} value={form.billType} onChange={e => set('billType', e.target.value)}>
                                        <option value="">Select type...</option>
                                        {FEDERAL_BILL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '1.25rem' }}>
                                    <div>
                                        <label style={lStyle}>Bill number</label>
                                        <input style={iStyle} type="text" placeholder="e.g. 1234"
                                            value={form.billNumber} onChange={e => set('billNumber', e.target.value)} />
                                    </div>
                                    <div>
                                        <label style={lStyle}>Congress session (optional)</label>
                                        <input style={iStyle} type="text" placeholder="e.g. 119th Congress"
                                            value={form.billSession} onChange={e => set('billSession', e.target.value)} />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* State bill by number */}
                        {form.inputMethod === 'number' && form.level === 'state' && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>State</label>
                                    <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                        <option value="">Select state...</option>
                                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Bill number / identifier</label>
                                    <input style={iStyle} type="text" placeholder="e.g. HB 1234 or SB 567"
                                        value={form.stateBillNum} onChange={e => set('stateBillNum', e.target.value)} />
                                </div>
                            </>
                        )}

                        {/* Paste bill text */}
                        {form.inputMethod === 'paste' && (
                            <>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Bill name or title (optional)</label>
                                    <input style={iStyle} type="text" placeholder="e.g. Save America Act, Infrastructure Investment Act 2026..."
                                        value={form.billTitle} onChange={e => set('billTitle', e.target.value)} />
                                </div>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={lStyle}>Full bill text — paste from congress.gov or state legislature website *</label>
                                    <textarea style={{ ...iStyle, resize: 'vertical' }} rows={10}
                                        placeholder="Copy and paste the full legal text of the bill here. Go to congress.gov, search for the bill, click 'Text' tab, then copy and paste the text here..."
                                        value={form.billText} onChange={e => set('billText', e.target.value)} />
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: form.billText.length > 100 ? '#3b6d11' : '#8a8070', marginTop: '3px' }}>
                                        {form.billText.length === 0
                                            ? 'Paste the bill text above to continue'
                                            : form.billText.length < 100
                                                ? `${form.billText.length} characters — paste more text for a complete analysis`
                                                : `✓ ${form.billText.length.toLocaleString()} characters — ready to analyze`}
                                    </div>
                                </div>
                                <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.25rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.7 }}>
                                    <strong style={{ color: '#1e2a3a' }}>How to get bill text:</strong><br />
                                    <strong>Federal:</strong> Go to <a href="https://congress.gov" target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}>congress.gov</a> → search bill name or number → click the bill → click "Text" tab → copy all text<br />
                                    <strong>State:</strong> Search "[your state] legislature" → find the bill → copy the text
                                </div>
                            </>
                        )}

                        <button style={{ ...btnPrimary, opacity: !canProceedStep1() ? 0.5 : 1 }}
                            disabled={!canProceedStep1()}
                            onClick={() => setStep(2)}>
                            Next: Select analysis →
                        </button>
                    </div>
                )}

                {/* ── STEP 2: Analysis selection ── */}
                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — What do you want to know?
                        </div>

                        {/* Bill summary bar */}
                        <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.5rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060' }}>
                            Analyzing: <strong style={{ color: '#1e2a3a' }}>
                                {form.inputMethod === 'paste'
                                    ? (form.billTitle || 'Pasted bill text')
                                    : form.level === 'federal'
                                        ? `${form.billType.split(' ')[0]} ${form.billNumber}${form.billSession ? ` (${form.billSession})` : ''}`
                                        : `${form.state} ${form.stateBillNum}`}
                            </strong>
                        </div>

                        {/* Analysis type checkboxes */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Select analysis sections (all selected by default)</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {ANALYSIS_TYPES.map(a => (
                                    <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: form.analysisTypes.includes(a.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.analysisTypes.includes(a.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{a.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{a.desc}</div>
                                        </div>
                                        <input type="checkbox"
                                            checked={form.analysisTypes.includes(a.id)}
                                            onChange={() => toggleAnalysis(a.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Additional context */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Additional context or specific questions (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="e.g. Focus on how this affects small business owners. Or: Does this conflict with the 4th Amendment specifically?..."
                                value={form.context} onChange={e => set('context', e.target.value)} />
                        </div>

                        {/* Note about bill number analysis */}
                        {form.inputMethod === 'number' && (
                            <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '10px 14px', marginBottom: '1.5rem', fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                <strong style={{ color: '#1e2a3a' }}>Note:</strong> For best results with bill numbers, paste the actual bill text in Step 1.
                                Analysis by number relies on training data and may not reflect the most current version of the bill.
                                For the latest text visit <a href="https://congress.gov" target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}>congress.gov</a>.
                            </div>
                        )}

                        {error && (
                            <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: form.analysisTypes.length === 0 || loading ? 0.6 : 1 }}
                                disabled={form.analysisTypes.length === 0 || loading}
                                onClick={generate}>
                                {loading ? 'Analyzing bill...' : 'Generate Analysis →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Analyzing constitutional implications, fiscal impact, and drafting your report...
                                <br />This may take 20-30 seconds for a thorough analysis.
                            </div>
                        )}
                    </div>
                )}

                {/* ── STEP 3: Result ── */}
                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                    Analysis complete
                                </div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>
                                    {result.billIdentifier}
                                </div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Report
                            </button>
                        </div>

                        {/* Analysis display */}
                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) {
                                    return (
                                        <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa', letterSpacing: '0.04em' }}>
                                            {line.replace('## ', '')}
                                        </div>
                                    );
                                }
                                if (line.startsWith('- ') || line.startsWith('• ')) {
                                    return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '2px' }}>• {line.slice(2)}</div>;
                                }
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>
                                ⬇ Download Report
                            </button>
                            <button onClick={reset} style={btnSecondary}>
                                Analyze another bill
                            </button>
                        </div>

                        {/* Disclaimer */}
                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This analysis is generated by AI and is intended for educational and civic engagement purposes.
                            It is not legal advice. For bill numbers without pasted text, analysis is based on available training data
                            and may not reflect the most current version. Always verify against the official bill text at
                            congress.gov or your state legislature's website.
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default BillAnalysisPage;
